// Import the RTK Query methods from the React-specific entry point
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { ExtendedDayTypes, ODUIMode } from 'appConstants';
import { ProcessStatus, TimeString } from 'appTypes';
import {
  Feature,
  FeatureCollection,
  LineString,
  MultiLineString,
} from 'geojson';

import { urlifyRouteIds } from './apiSlice';
import { RootState } from './store';
import { baseQuery } from './utils';

export type ODUIModeValue = (typeof ODUIMode)[keyof typeof ODUIMode];

export enum DayTypeSetting {
  BY_DAY_TYPE = 'BY_DAY_TYPE',
  COMBINED = 'COMBINED',
}

type DayTypeSettingType = `${DayTypeSetting}`;

export type ODAnalysis = {
  readonly analysis_id: string;
  name?: string;
  readonly status: ProcessStatus;
  tt_seg_ids: Array<string>;
  segment_display_names: Array<string>;
  readonly project_slug: string;
  layer: number;
  start_date: string;
  end_date: string;
  day_type_setting: DayTypeSettingType;
  time_period: TimePeriod;
  include_mtr: boolean;
  has_volume: boolean;
  readonly created_at: string; // ISO date string
  readonly created_by?: string; // username
  readonly error_message?: string;
};

type CreateODAnalysis = Omit<
  ODAnalysis,
  | 'analysis_id'
  | 'status'
  | 'project_slug'
  | 'created_at'
  | 'created_by'
  | 'error_message'
>;

export const originDestinationApiSlice = createApi({
  reducerPath: 'origindestination_api',
  baseQuery,
  tagTypes: ['analyses'],
  endpoints: (builder) => ({
    getAnalyses: builder.query<ODAnalysis[], string>({
      query: (project_slug) => {
        return `/od_analysis/${project_slug}/`;
      },
      providesTags: (result, error, project_slug) => [
        { type: 'analyses', id: project_slug },
      ],
    }),
    getAnalysisMatrix: builder.query<
      {
        x: AnalysisSegment[];
        y: AnalysisSegment[];
        matrix: number[][];
        start_time: TimeString;
        end_time: TimeString;
        day_type: ExtendedDayTypes;
      },
      {
        project_slug: string;
        analysis_id: string;
        startTime: TimeString;
        endTime: TimeString;
        dayType: ExtendedDayTypes;
      }
    >({
      query: ({ project_slug, analysis_id, startTime, endTime, dayType }) => {
        const params = new URLSearchParams({
          start_time: startTime,
          end_time: endTime,
          day_type: dayType,
        });
        return `/od_analysis/${project_slug}/${analysis_id}/od_matrix/?${params}`;
      },
    }),
    getAnalysisStatus: builder.query<
      { status: ProcessStatus },
      { project_slug: string; analysis_id: string }
    >({
      query: ({ project_slug, analysis_id }) => {
        return `/od_analysis/${project_slug}/${analysis_id}/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { status: rawResult.status };
      },
    }),
    createAnalysis: builder.mutation<
      ODAnalysis,
      { project_slug: string; analysis: CreateODAnalysis }
    >({
      query: ({ project_slug, analysis }) => {
        return {
          url: `/od_analysis/${project_slug}/`,
          method: 'POST',
          body: analysis,
        };
      },
      invalidatesTags: (result, error, { project_slug }) => [
        { type: 'analyses', id: project_slug },
      ],
    }),
    /** tomtom segments */
    getSegmentGeometries: builder.query<
      Feature<LineString>[],
      { layerId: number; segIds: Array<string> }
    >({
      query: ({ layerId, segIds }) => {
        return `/od_analysis/segments/${layerId}/${urlifyRouteIds(segIds)}/`;
      },
      transformResponse: (
        rawResult: { segments: Map<string, any> },
        meta,
        arg,
      ) => {
        return Object.values(rawResult.segments);
      },
    }),
    getMtrGeojson: builder.query<
      FeatureCollection<MultiLineString>,
      { project_slug: string; analysis_id: string }
    >({
      query: ({ project_slug, analysis_id }) => {
        return `/od_analysis/${project_slug}/${analysis_id}/mtr.geojson`;
      },
    }),
  }),
});

export const {
  useGetAnalysesQuery,
  useGetAnalysisMatrixQuery,
  useGetAnalysisStatusQuery,
  useCreateAnalysisMutation,
  useLazyGetSegmentGeometriesQuery,
  useGetMtrGeojsonQuery,
  useLazyGetMtrGeojsonQuery,
} = originDestinationApiSlice;

export enum TimePeriod {
  MONTH = 'MONTH',
  RANGE = 'RANGE',
}

type AnalysisSegment = {
  id: string;
  name: string;
};

type ODState = {
  uiMode: ODUIModeValue;
  analysisStatus?: ProcessStatus;
  analysisSegments: AnalysisSegment[];
  analysisName?: string;
  visualize: boolean;
  showViewAllModal: boolean;
  analysisStart: string;
  analysisEnd: string;
  analysisIsSaving: boolean; // transient state between confirming “Run Analysis” and receiving the updated list from the server.
  dayTypeSetting: DayTypeSettingType;
  timePeriod: TimePeriod;
  analysisHasVolume?: boolean;
  hoveredSegmentIds: string[] | null;
  clickedMatrixSegmentIds: string[] | null;
  includeMtr: boolean;
};

const initialState: ODState = {
  uiMode: ODUIMode.View,
  analysisStatus: undefined,
  analysisSegments: [],
  analysisName: '',
  visualize: false,
  showViewAllModal: false,
  analysisIsSaving: false,
  analysisStart: undefined,
  analysisEnd: undefined,
  dayTypeSetting: DayTypeSetting.BY_DAY_TYPE,
  timePeriod: TimePeriod.MONTH,
  hoveredSegmentIds: null,
  clickedMatrixSegmentIds: null,
  includeMtr: false,
};

export const originDestinationSlice = createSlice({
  name: 'origindestination',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetAnalysisData: (state) => {
      return {
        ...initialState,
        uiMode: state.uiMode,
        analysisSegments: state.analysisSegments,
      };
    },
    resetAll: () => initialState,
    setAnalysisStatus: (state, action) => {
      if (action.payload !== state.analysisStatus) {
        state.analysisStatus = action.payload;
      }
    },
    setMatrixVisualize: (state, action) => {
      if (action.payload !== state.analysisStatus) {
        state.visualize = action.payload;
      }
    },
    setAnalysisIsSaving: (state, action) => {
      state.analysisIsSaving = action.payload;
    },
    setShowViewAllModal: (state, action) => {
      state.showViewAllModal = action.payload;
    },
    setAnalysisSegments: (state, action) => {
      state.analysisSegments = action.payload;
    },
    addAnalysisSegment: (state, action) => {
      state.analysisSegments = [
        ...(state.analysisSegments || []),
        action.payload,
      ];
    },
    undoAnalysisSegment: (state) => {
      const arr = state.analysisSegments || [];
      state.analysisSegments = arr.slice(0, -1);
    },
    removeAnalysisSegment: (state, action) => {
      state.analysisSegments = state.analysisSegments.filter(
        (el) => el.id !== action.payload,
      );
    },
    editAnalysisSegmentName: (
      state,
      action: PayloadAction<{ id: string; name: string }>,
    ) => {
      const { id, name } = action.payload;
      const segment = state.analysisSegments?.find((s) => s.id === id);
      if (segment) segment.name = name;
    },
    setAnalysisName: (state, action) => {
      state.analysisName = action.payload;
    },
    setODUIMode: (state, action: PayloadAction<ODUIModeValue>) => {
      state.uiMode = action.payload;
    },
    setAnalysisStartDate: (state, action) => {
      state.analysisStart = action.payload;
    },
    setAnalysisEndDate: (state, action) => {
      state.analysisEnd = action.payload;
    },
    setDayTypeSetting: (state, action) => {
      state.dayTypeSetting = action.payload;
    },
    setIncludeMtr: (state, action) => {
      state.includeMtr = action.payload;
    },
    setTimePeriod: (state, action) => {
      state.timePeriod = action.payload;
    },
    setAnalysisHasVolume: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.analysisHasVolume = action.payload;
    },
    setHoveredSegmentIds: (state, action) => {
      state.hoveredSegmentIds = action.payload;
    },
    setClickedMatrixSegmentIds: (state, action) => {
      state.clickedMatrixSegmentIds = action.payload;
    },
    copyAnalysis: (state, action) => {
      state.visualize = false;
      const analysis = action.payload as ODAnalysis;
      state.analysisStatus = undefined;
      state.analysisName = `Copy of ${analysis.name}`;
      state.analysisEnd = analysis.end_date;
      state.analysisSegments = analysis.tt_seg_ids.map((id, i) => ({
        id,
        name: analysis.segment_display_names[i],
      }));
      state.analysisStart = analysis.start_date;
      state.analysisIsSaving = false;
      state.dayTypeSetting = analysis.day_type_setting;
      state.includeMtr = analysis.include_mtr;
      state.timePeriod = analysis.time_period;
      state.analysisHasVolume = analysis.has_volume;
    },
  },
});

const { actions } = originDestinationSlice;
export const {
  setAnalysisStatus,
  setAnalysisSegments,
  setAnalysisName,
  resetAll,
  setShowViewAllModal,
  setODUIMode,
  setAnalysisIsSaving,
  setAnalysisStartDate,
  setAnalysisEndDate,
  resetAnalysisData,
  setDayTypeSetting,
  setTimePeriod,
  setAnalysisHasVolume,
  addAnalysisSegment,
  undoAnalysisSegment,
  removeAnalysisSegment,
  copyAnalysis,
  editAnalysisSegmentName,
  setHoveredSegmentIds,
  setClickedMatrixSegmentIds,
  setMatrixVisualize,
  setIncludeMtr,
} = actions;

export function getODState(rootState) {
  return rootState[originDestinationSlice.name];
}
export function selectAnalysisIsSaving(rootState: RootState): boolean {
  return getODState(rootState).analysisIsSaving;
}

export function selectMatrixVisualize(rootState: RootState): boolean {
  return getODState(rootState).visualize;
}

export function selectShowViewAllModal(rootState: RootState): boolean {
  return getODState(rootState).showViewAllModal;
}

export function selectAnalysisStatus(rootState): ProcessStatus {
  return getODState(rootState).analysisStatus;
}

export function selectAnalysisSegments(rootState): AnalysisSegment[] {
  return getODState(rootState).analysisSegments;
}

export function selectAnalysisName(rootState): string {
  return getODState(rootState).analysisName;
}

export function selectODUIMode(rootState): ODUIModeValue {
  return getODState(rootState).uiMode;
}
export function selectAnalysisStartDate(rootState) {
  return getODState(rootState).analysisStart;
}
export function selectAnalysisEndDate(rootState) {
  return getODState(rootState).analysisEnd;
}
export function selectDayTypeSetting(rootState) {
  return getODState(rootState).dayTypeSetting;
}
export function selectTimePeriod(rootState) {
  return getODState(rootState).timePeriod;
}
export function selectAnalysisHasVolume(rootState): boolean | undefined {
  return getODState(rootState).analysisHasVolume;
}
export function selectHoveredSegmentIds(rootState) {
  return getODState(rootState).hoveredSegmentIds;
}
export function selectClickedMatrixSegmentIds(rootState) {
  return getODState(rootState).clickedMatrixSegmentIds;
}
export function selectIncludeMtr(rootState): boolean {
  return getODState(rootState).includeMtr;
}
